/**
 * Persists the RFQ Create Wizard state as a real RFQ (header, lines, costs,
 * certificates) and optionally sends it to the selected suppliers.
 *
 * Extracted from RFQCreateWizard so the wizard stays a view-state
 * component; this module owns the API call sequence.
 */
import { rfqAPI } from '../../lib/api';
import type {
  BasicDetailsForm,
  LineItemForm,
  AdditionalCostForm,
  RequiredCertificateForm,
} from './rfqWizardTypes';

export interface CreateRfqFromWizardInput {
  /** Helix admins must name the customer; customer users leave undefined
   *  (the backend stamps their active company). */
  customerId?: number;
  /** Currency the RFQ is raised in — the tenant's reporting currency. */
  currency: string;
  basicDetails: BasicDetailsForm;
  lineItems: LineItemForm[];
  additionalCosts: AdditionalCostForm[];
  requiredCertificates: RequiredCertificateForm[];
  /** The NDA file to attach when basicDetails.nda_required is set. Uploaded
   *  as an RFQDocument of type 'nda' straight after the RFQ is created, so
   *  the send step always finds it. */
  ndaFile?: File | null;
  supplierIds: number[];
  sendToSuppliers: boolean;
}

export interface CreateRfqFromWizardResult {
  rfqId: number;
  /** True when the RFQ was also sent to suppliers. */
  sent: boolean;
}

/**
 * Create the RFQ and its children, then send it when asked.
 *
 * @returns the new RFQ id and whether it was sent
 */
export async function createRfqFromWizard(input: CreateRfqFromWizardInput): Promise<CreateRfqFromWizardResult> {
  const { customerId, currency, basicDetails, lineItems, additionalCosts, requiredCertificates, ndaFile, supplierIds, sendToSuppliers } = input;

  // CR-5466 wizard rebuild: send only the fields the redesigned
  // Basic Details step collects. Title is no longer surfaced; derive
  // a sensible default so the model has something readable in lists
  // (Reference > Description excerpt > rfq_number-style fallback).
  // CR-5468: for a customer user the backend stamps the customer FK from
  // their active company and ignores anything sent. A Helix admin has no
  // customer of their own, so the payload names the one selected in the
  // top bar.
  const derivedTitle = (basicDetails.reference || basicDetails.description || '').trim().slice(0, 200);
  const rfqResponse = await rfqAPI.createRFQ({
    ...(customerId ? { customer: customerId } : {}),
    title: derivedTitle,
    description: basicDetails.description,
    response_deadline: basicDetails.response_deadline,
    nda_required: basicDetails.nda_required,
    currency,
    // CR-5466 additions
    reference: basicDetails.reference || undefined,
    rfq_type: basicDetails.rfq_type ?? undefined,
    issue_date: basicDetails.issue_date || undefined,
  });
  const rfqId = rfqResponse.data.id;

  // Attach the NDA before anything else so a failure surfaces while the RFQ
  // is still an un-sent draft. The backend refuses to send an NDA-required
  // RFQ that has no NDA document, so this is what makes sending possible.
  if (basicDetails.nda_required && ndaFile) {
    const form = new FormData();
    form.append('rfq', String(rfqId));
    form.append('document_type', 'nda');
    form.append('title', 'Non-Disclosure Agreement');
    form.append('description', 'Suppliers must accept this NDA before viewing or responding to the RFQ.');
    form.append('is_mandatory', 'true');
    // Explicit: a boolean missing from multipart reads as False server-side.
    form.append('is_active', 'true');
    form.append('file', ndaFile, ndaFile.name);
    await rfqAPI.uploadDocument(form);
  }

  for (let i = 0; i < lineItems.length; i++) {
    const item = lineItems[i];
    await rfqAPI.createLineItem({
      rfq: rfqId,
      line_number: i + 1,
      item_description: item.item_description,
      part_number: item.part_number,
      quantity: item.quantity,
      unit_of_measure: item.unit_of_measure,
      target_unit_price: item.target_unit_price || undefined,
      // CR-5467: drawing_issue is the new line-level revision field;
      // delivery_location / required_delivery_date are intentionally
      // not sent — they're moving to the RFQ header in a follow-up.
      drawing_issue: item.drawing_issue,
      technical_specifications: item.technical_specifications,
      notes: item.notes,
    });
  }

  for (const cost of additionalCosts) {
    await rfqAPI.createAdditionalCost({
      rfq: rfqId,
      cost_type: cost.cost_type,
      description: cost.description,
      amount: cost.amount,
      currency,
      notes: cost.notes,
    });
  }

  for (const cert of requiredCertificates) {
    await rfqAPI.createRequiredCertificate({
      rfq: rfqId,
      certificate_type: cert.certificate_type,
      certificate_name: cert.certificate_name,
      description: cert.description,
      is_mandatory: cert.is_mandatory,
      expiry_tolerance_days: cert.expiry_tolerance_days,
    });
  }

  const sent = sendToSuppliers && supplierIds.length > 0;
  if (sent) {
    await rfqAPI.sendToSuppliers(rfqId, { supplier_ids: supplierIds });
  }
  return { rfqId, sent };
}
